export type RGB = [number, number, number];
type HSL = [number, number, number];

// COLOR converters - taken from internet :)
export function getRgbColorFromHex(hex: string): RGB {
    if (hex.substring(0, 1) === "#") {
        hex = hex.slice(1);
    }
    const value = parseInt(hex, 16);
    const r = (value >> 16) & 255;
    const g = (value >> 8) & 255;
    const b = value & 255;

    return [r, g, b] as RGB;
}

function getHslFromRGB(rgb: RGB): HSL | null {
    const r = rgb[0] / 255;
    const g = rgb[1] / 255;
    const b = rgb[2] / 255;
    const cmin = Math.min(r, g, b);
    const cmax = Math.max(r, g, b);
    const delta = cmax - cmin;
    let h: number;
    let s: number;
    let l: number;

    if (delta === 0) h = 0;
    else if (cmax === r) h = ((g - b) / delta) % 6;
    else if (cmax === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;

    h = Math.round(h * 60);

    if (h < 0) h += 360;

    l = (cmax + cmin) / 2;
    s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
    s = +(s * 100).toFixed(1);
    l = +(l * 100).toFixed(1);

    return [h, s, l];
}

function getRGBFromHSL(hsl: HSL): RGB {
    const s = hsl[1] / 100;
    const l = hsl[2] / 100;
    const k = (n: number) => (n + hsl[0] / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return [Math.round(255 * f(0)), Math.round(255 * f(8)), Math.round(255 * f(4))];
}

export function getContrast(foreground: RGB, background: RGB): number {
    const foregroundLuminance = luminance(foreground);
    const backgroundLuminance = luminance(background);

    const brightest = Math.max(foregroundLuminance, backgroundLuminance);
    const darkest = Math.min(foregroundLuminance, backgroundLuminance);

    return (brightest + 0.05) / (darkest + 0.05);
}

function luminance(rgb: RGB): number {
    const [r, g, b] = rgb.map((v) => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return r * 0.2126 + g * 0.7152 + b * 0.0722;
}

function getOptimisedBackgroundColor(foregroundRGB: RGB, backgroundRGB: RGB): RGB {
    const contrast = getContrast(foregroundRGB, backgroundRGB);

    if (contrast > 3) {
        return backgroundRGB;
    }

    const foregroundHSL: HSL | null = getHslFromRGB(foregroundRGB);
    const backgroundHSL: HSL | null = getHslFromRGB(backgroundRGB);

    if (!foregroundHSL || !backgroundHSL) {
        return backgroundRGB;
    }

    // console.log(backgroundHSL[2]);
    const newLightness = backgroundHSL[2] - 1;

    return getOptimisedBackgroundColor(foregroundRGB, getRGBFromHSL([backgroundHSL[0], backgroundHSL[1], newLightness]));
}

export function optimiseBackgroundColor(foreground: string, background: string): string {
    const foregroundRGB: RGB = getRgbColorFromHex(foreground);
    const backgroundRGB: RGB = getRgbColorFromHex(background);

    const newRGB: RGB = getOptimisedBackgroundColor(foregroundRGB, backgroundRGB);

    return `${newRGB[0]} ${newRGB[1]} ${newRGB[2]}`;
}

export function getRgbColorFromHexAsString(hex: string): string {
    const rgb: RGB = getRgbColorFromHex(hex);
    return `${rgb[0]} ${rgb[1]} ${rgb[2]}`;
}
